import { Col, Flex, Grid, Text } from '@tremor/react';
import { LoaderFunctionArgs, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ConnectorFromStore } from '@/types/Connectors';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { assert } from '@/utils/asserts';
import { ConnectorTitle } from '@/components/layouts/Connectors/ConnectorTitle/ConnectorTitle';
import { ConnectorCard } from '@/components/Cards/ConnectorCard/ConnectorCard';
import { ConnectorsService } from '@/services/connectorsService';
import { useOrganizationStore } from '@/stores/organizationStore';
import { some } from 'lodash';
import { ConnectorFromStoreSchema } from '@/services/connectorSchema';
import { useWorkspaceConnectors } from '@/services/queries/useWorkspaceConnectors';
import { hasPermission } from '@/auth/hasPermission';
import { AddButton } from '@/components/layouts/Buttons/AddButton/AddButton';

export interface LocalConnectorData extends ConnectorFromStore {
  isAnyAccountUpdating: boolean;
}

export function parseConnectors(connectors: LocalConnectorData[]): LocalConnectorData[] {
  const parsedConnectors: LocalConnectorData[] = [];

  connectors.forEach((connector) => {
    const result = ConnectorFromStoreSchema.safeParse(connector);
    if (result.success) {
      const isConnectorChangingState =
        connector.status === 'ACTIVATION_AWAITING' ||
        connector.status === 'DEACTIVATION_AWAITING';
      const localConnector = {
        ...connector,
        isAnyAccountUpdating: isConnectorChangingState
          ? false
          : some(
              connector.config.accounts,
              (adAccount) =>
                !adAccount.status.includes('SUCCESS') &&
                !adAccount.status.includes('FAILURE'),
            ),
      } as LocalConnectorData;

      parsedConnectors.push(localConnector);
    }
  });

  return parsedConnectors;
}

export function Connectors() {
  const workspaceId = useWorkspaceIdFromParams();
  assert(workspaceId, 'Workspace ID is not defined');
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const navigate = useNavigate();
  assert(selectedOrganization, 'Organization is not defined');

  const rawConnectors = useWorkspaceConnectors(workspaceId);

  const connectors = parseConnectors(rawConnectors);

  const hasPermissionToAddConnector = hasPermission('workspace', 'connectors', 'create');

  const addConnectorTooltip = !hasPermissionToAddConnector
    ? "You don't have permissions required to add new connector"
    : undefined;

  return (
    <>
      <div className="w-full">
        <Flex>
          <ConnectorTitle prefix={false}>Connectors</ConnectorTitle>
          <AddButton
            className="!rounded-xl hover:bg-primary-700 transition-colors duration-200"
            onClick={() => {
              navigate(`list?workspaceId=${workspaceId}`, { relative: 'route' });
            }}
            disabled={!hasPermissionToAddConnector}
            tooltip={addConnectorTooltip}
          >
            Add Connector
          </AddButton>
        </Flex>
        <Grid numCols={2} className="gap-6 mt-5">
          {connectors.length === 0 ? (
            <Flex>
              <Text>No connectors added</Text>
            </Flex>
          ) : (
            connectors.map((connector) => (
              <Col key={connector.id}>
                <ConnectorCard
                  isInitialSetup={connector.isInitialSetup}
                  isSyncing={connector.isAnyAccountUpdating}
                  connectorName={connector.name}
                  createdDate={moment(connector.created_date.toDate()).format(
                    'YYYY-MM-DD HH:mm',
                  )}
                  createdBy={connector.created_by}
                  lastModifiedBy={connector.last_modified_by}
                  lastModifiedDate={moment(connector.last_modified_date.toDate()).format(
                    'YYYY-MM-DD HH:mm',
                  )}
                  status={connector.status}
                  connectorType={connector.id}
                  locationCode={connector.locationCode}
                  timezone={connector.timezone}
                />
              </Col>
            ))
          )}
        </Grid>
      </div>
    </>
  );
}

export async function connectorsLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const workspaceId = url.searchParams.get('workspaceId') as string;
  return await ConnectorsService.getConnectors(workspaceId);
}
